// Position Validation System

export class PositionValidator {
  constructor(testType) {
    this.testType = testType;
    this.validationErrors = [];
  }

  // Main validation function
  validatePosition(keypoints) {
    this.validationErrors = [];

    if (!keypoints || keypoints.length === 0) {
      return {
        valid: false,
        errors: ['No person detected in frame'],
        confidence: 0
      };
    }

    // Check overall confidence
    const avgConfidence = this.getAverageConfidence(keypoints);
    if (avgConfidence < 0.4) {
      this.validationErrors.push('⚠️ Low detection quality - improve lighting');
      return { valid: false, errors: this.validationErrors, confidence: avgConfidence };
    }

    // Test-specific validation
    switch(this.testType) {
      case 'situps':
        return this.validateSitupPosition(keypoints);
      case 'vertical_jump':
        return this.validateJumpPosition(keypoints);
      case 'shuttle_run':
        return this.validateRunPosition(keypoints);
      default:
        return { valid: true, errors: [], confidence: avgConfidence };
    }
  }

  // Situp position validation
  validateSitupPosition(keypoints) {
    const required = ['left_hip', 'right_hip', 'left_knee', 'right_knee', 
                      'left_shoulder', 'right_shoulder'];
    
    // Check all required points visible
    const missing = required.filter(name => {
      const kp = keypoints.find(k => k.name === name);
      return !kp || kp.score < 0.3;
    });

    if (missing.length > 0) {
      this.validationErrors.push(`❌ Cannot see: ${missing.join(', ')}`);
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    // Get keypoints
    const leftHip = keypoints.find(k => k.name === 'left_hip');
    const rightHip = keypoints.find(k => k.name === 'right_hip');
    const leftKnee = keypoints.find(k => k.name === 'left_knee');
    const rightKnee = keypoints.find(k => k.name === 'right_knee');
    const leftShoulder = keypoints.find(k => k.name === 'left_shoulder');
    const rightShoulder = keypoints.find(k => k.name === 'right_shoulder');

    const hipY = (leftHip.y + rightHip.y) / 2;
    const kneeY = (leftKnee.y + rightKnee.y) / 2;
    const shoulderY = (leftShoulder.y + rightShoulder.y) / 2;

    // Check 1: Person should be horizontal (lying down)
    const bodyAngle = this.calculateBodyAngle(leftShoulder, leftHip, leftKnee);
    
    if (bodyAngle > 45 && bodyAngle < 135) {
      // Person is sitting or standing, not lying down
      this.validationErrors.push('❌ Wrong position - You must lie down on your back');
      this.validationErrors.push('💡 Tip: Lie flat with knees bent');
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    // Check 2: Knees should be bent (not straight legs)
    const legAngle = this.calculateAngle(leftHip, leftKnee, 
      keypoints.find(k => k.name === 'left_ankle'));
    
    if (legAngle > 150) {
      this.validationErrors.push('⚠️ Bend your knees at 90 degrees');
      return { valid: false, errors: this.validationErrors, confidence: 0.3 };
    }

    // Check 3: Hips should be lower than knees (lying position)
    if (hipY < kneeY - 50) {
      this.validationErrors.push('❌ Wrong orientation - Lie down properly');
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    // All checks passed
    return { 
      valid: true, 
      errors: [], 
      confidence: this.getAverageConfidence(keypoints),
      message: '✅ Perfect position!'
    };
  }

  // Vertical jump validation
  validateJumpPosition(keypoints) {
    const leftHip = keypoints.find(k => k.name === 'left_hip');
    const rightHip = keypoints.find(k => k.name === 'right_hip');
    const leftAnkle = keypoints.find(k => k.name === 'left_ankle');
    const rightAnkle = keypoints.find(k => k.name === 'right_ankle');

    if (!leftHip || !rightHip || !leftAnkle || !rightAnkle) {
      this.validationErrors.push('❌ Full body must be visible');
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    // Check 1: Person should be standing upright
    const hipY = (leftHip.y + rightHip.y) / 2;
    const ankleY = (leftAnkle.y + rightAnkle.y) / 2;

    if (ankleY < hipY) {
      this.validationErrors.push('❌ Wrong position - Stand upright');
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    // Check 2: Both feet visible
    if (leftAnkle.score < 0.3 || rightAnkle.score < 0.3) {
      this.validationErrors.push('⚠️ Keep both feet visible in frame');
      return { valid: false, errors: this.validationErrors, confidence: 0.3 };
    }

    return { 
      valid: true, 
      errors: [], 
      confidence: this.getAverageConfidence(keypoints),
      message: '✅ Ready to jump!'
    };
  }

  // Shuttle run validation
  validateRunPosition(keypoints) {
    const required = ['left_hip', 'right_hip', 'left_ankle', 'right_ankle'];
    
    const missing = required.filter(name => {
      const kp = keypoints.find(k => k.name === name);
      return !kp || kp.score < 0.3;
    });

    if (missing.length > 0) {
      this.validationErrors.push('❌ Full body must be visible');
      return { valid: false, errors: this.validationErrors, confidence: 0 };
    }

    return { 
      valid: true, 
      errors: [], 
      confidence: this.getAverageConfidence(keypoints),
      message: '✅ Position correct!'
    };
  }

  // Video analysis validation
  validateVideoAnalysis(frames, testType) {
    const results = {
      valid: true,
      errors: [],
      warnings: [],
      confidence: 0,
      totalFrames: frames.length,
      validFrames: 0
    };

    if (frames.length < 10) {
      results.valid = false;
      results.errors.push('❌ Video too short - minimum 2 seconds required');
      return results;
    }

    let totalConfidence = 0;
    let validCount = 0;
    let positionErrors = 0;

    frames.forEach((frame, index) => {
      const validation = this.validatePosition(frame.keypoints);
      
      if (validation.valid) {
        validCount++;
        totalConfidence += validation.confidence;
      } else {
        positionErrors++;
      }
    });

    results.validFrames = validCount;
    results.confidence = validCount > 0 ? totalConfidence / validCount : 0;

    // Calculate error ratio
    const errorRatio = positionErrors / frames.length;

    if (errorRatio > 0.5) {
      results.valid = false;
      results.errors.push('❌ Wrong position detected in most of the video');
      
      if (testType === 'situps') {
        results.errors.push('💡 You must lie down flat on your back');
        results.errors.push('💡 Bend your knees at 90 degrees');
      } else if (testType === 'vertical_jump') {
        results.errors.push('💡 Stand upright before jumping');
        results.errors.push('💡 Keep full body in frame');
      }
    } else if (errorRatio > 0.3) {
      results.warnings.push('⚠️ Some frames have incorrect position');
      results.warnings.push('💡 Maintain proper form throughout');
    }

    if (results.confidence < 0.4) {
      results.valid = false;
      results.errors.push('❌ Poor video quality or lighting');
      results.errors.push('💡 Record in well-lit area');
    }

    if (results.valid && results.errors.length === 0) {
      results.errors.push('✅ Video validated successfully!');
      results.errors.push(`✅ ${validCount}/${frames.length} frames have correct position`);
    }

    return results;
  }

  // Helper functions
  getAverageConfidence(keypoints) {
    if (!keypoints || keypoints.length === 0) return 0;
    const sum = keypoints.reduce((acc, kp) => acc + (kp.score || 0), 0);
    return sum / keypoints.length;
  }

  calculateAngle(a, b, c) {
    if (!a || !b || !c) return 180;
    const ab = { x: a.x - b.x, y: a.y - b.y };
    const cb = { x: c.x - b.x, y: c.y - b.y };
    const dot = ab.x * cb.x + ab.y * cb.y;
    const mag = Math.sqrt(ab.x * ab.x + ab.y * ab.y) * Math.sqrt(cb.x * cb.x + cb.y * cb.y);
    const cos = Math.max(-1, Math.min(1, dot / mag));
    return Math.acos(cos) * 180 / Math.PI;
  }

  calculateBodyAngle(shoulder, hip, knee) {
    if (!shoulder || !hip || !knee) return 90;
    
    // Calculate angle between vertical and body
    const bodyVector = { x: hip.x - shoulder.x, y: hip.y - shoulder.y };
    const verticalVector = { x: 0, y: 1 };
    
    const dot = bodyVector.x * verticalVector.x + bodyVector.y * verticalVector.y;
    const mag = Math.sqrt(bodyVector.x * bodyVector.x + bodyVector.y * bodyVector.y);
    const cos = Math.max(-1, Math.min(1, dot / mag));
    
    return Math.abs(Math.acos(cos) * 180 / Math.PI);
  }
}

export default PositionValidator;